/**
 * Underlying quote-overlay registry: the frontend SSOT for every
 * instrument drawn as rebased hollow candles over the ES chart.
 *
 * Consumed by:
 *  - UnderlyingQuoteIndicator (per-key style)
 *  - ESChart (overlay list + one indicator-instance map +
 *    the quoteOverlays prop shape)
 *  - IndicatorSidePanel (the quote-overlay toggle rows)
 *  - App.jsx / planningIndicators.js (registry-derived toggle defaults)
 *  - renkoIndicators.js (strip list: the renko page wires no overlays)
 *
 * Adding an instrument here is the ONLY frontend change a new overlay
 * needs (the backend counterpart is src/config.py AUX_FUT_UNDERLYINGS +
 * a registry entry + a contracts JSON; keys must match lowercase).
 *
 * BOUNDARY: this registry is exclusively for ES-scale REBASED overlays;
 * pane-class instruments do not belong here. (VIX JOINED as a rebased
 * overlay 2026-08-11, replacing its earlier sub-pane: its noAutoscale
 * style flag keeps it from ever influencing the shared ES axis, since
 * VIX moves 5-30% a day vs bps-1% for the others.)
 *
 * kind: 'cash' overlays with converter ratios (spx/spy) have strike
 * identity: a rebased candle touching a level IS a strike touch,
 * RTH-only. 'fut' overlays rebase by a raw ES/root T-1 close ratio: a
 * pure visual alignment scalar, relative divergence ONLY, drawing the
 * full ~23h Globex session. vix is cash-family by data source and hours
 * but anchors AUX-STYLE (raw ES/VIX T-1 close ratio ~500, no strike
 * identity, relative change only).
 */

export const UNDERLYING_OVERLAYS = [
  {
    key: 'spx', toggleId: 'spxLine', kind: 'cash',
    description: 'Real SPX price line (Schwab stream, RTH)',
    style: { up: '#e0b040', down: '#9a7420', title: 'SPX', decimals: 1 },
  },
  {
    key: 'spy', toggleId: 'spyLine', kind: 'cash',
    description: 'Real SPY price line (RTH)',
    style: { up: '#40c4aa', down: '#2a8571', title: 'SPY', decimals: 2 },
  },
  {
    key: 'nq', toggleId: 'nqLine', kind: 'fut',
    description: 'NQ futures tape rebased onto the ES scale (relative divergence only, no strike identity; draws overnight)',
    style: { up: '#9b7bff', down: '#6a4fc4', title: 'NQ', decimals: 2 },
  },
  {
    key: 'rty', toggleId: 'rtyLine', kind: 'fut',
    description: 'Russell 2000 futures tape rebased onto the ES scale (small-cap breadth; relative divergence only; draws overnight)',
    style: { up: '#fb923c', down: '#b45309', title: 'RTY', decimals: 1 },
  },
  {
    key: 'ym', toggleId: 'ymLine', kind: 'fut',
    description: 'Dow futures tape rebased onto the ES scale (value/industrial tilt; relative divergence only; draws overnight)',
    style: { up: '#60a5fa', down: '#2f5fa8', title: 'YM', decimals: 0 },
  },
  {
    key: 'zn', toggleId: 'znLine', kind: 'fut',
    description: '10-year Treasury futures tape rebased onto the ES scale (rates axis; low-vol so it hugs ES; relative divergence only; draws overnight)',
    style: { up: '#94a3b8', down: '#64748b', title: 'ZN', decimals: 3 },
  },
  {
    key: 'vix', toggleId: 'vixLine', kind: 'cash',
    description: 'Cboe VIX rebased at the T-1 close ratio, amplitude compressed 10:1 around the prior ES close (inverse mover; relative change only, no strike identity; equity hours; never influences the ES axis scale)',
    // noAutoscale: the series joins the shared right scale for pinned
    // comparability but contributes NOTHING to its autoscale (VIX moves
    // 5-30%/day: without this one vol spike squashes the ES candles).
    // gain: compress displayed amplitude around the anchor's ES T-1
    // close (overlayRebaseCoeffs below): at 1:1 a routine 5% VIX day
    // sits hundreds of ES points off-viewport (noAutoscale never
    // stretches the axis to it); 0.1 draws a 10% VIX day like a 1% ES
    // move (~75 pts). Amplitude is symbolic; direction vs T-1 is exact.
    style: { up: '#e0e070', down: '#8f8f45', title: 'VIX', decimals: 2, noAutoscale: true, gain: 0.1 },
  },
];

export const OVERLAY_KEYS = UNDERLYING_OVERLAYS.map((o) => o.key);
export const OVERLAY_TOGGLE_IDS = UNDERLYING_OVERLAYS.map((o) => o.toggleId);

/** The parent group toggle: overlays draw only while this is ON.
 *  Children keep their saved states across parent off/on cycles. */
export const OVERLAY_PARENT_ID = 'indexOverlays';

/** Every indicator-state id the overlay system owns (parent + children):
 *  drives the pages' overlayStateKey memo dep and the renko strip. */
export const OVERLAY_STATE_IDS = [OVERLAY_PARENT_ID, ...OVERLAY_TOGGLE_IDS];

/** Registry-derived toggle defaults: spread into INDICATOR_DEFAULTS maps. */
export const OVERLAY_TOGGLE_DEFAULTS = {
  [OVERLAY_PARENT_ID]: false,
  ...Object.fromEntries(UNDERLYING_OVERLAYS.map((o) => [o.toggleId, false])),
};

/** STYLE map for UnderlyingQuoteIndicator, keyed by overlay key. */
export const OVERLAY_STYLES = Object.fromEntries(
  UNDERLYING_OVERLAYS.map((o) => [o.key, o.style])
);

/**
 * Rebase coefficients for one overlay segment: displayed = m * price + b
 * (inverse: price = (v - b) / m for native-unit axis/crosshair labels).
 * SSOT for the transform, used by UnderlyingQuoteIndicator (candle
 * geometry + formatter) and the chart tooltip (relEs cell), so readouts
 * always match the drawn position.
 *
 * Plain rebase (m = anchor, b = 0) unless the overlay carries a gain
 * AND the segment carries anchorEs (the anchor's ES T-1 close
 * numerator, served as session-ratios 'vix_es'); then the amplitude
 * around anchorEs is compressed: displayed = E + g * (price * anchor - E)
 * => m = g * anchor, b = (1 - g) * E. Missing anchorEs (older sessions,
 * pre-restart backend) degrades to the uncompressed rebase.
 */
export const overlayRebaseCoeffs = (key, anchor, anchorEs) => {
  const gain = OVERLAY_STYLES[key]?.gain;
  if (gain == null || gain === 1 || anchorEs == null) {
    return { m: anchor, b: 0 };
  }
  return { m: gain * anchor, b: (1 - gain) * anchorEs };
};

/** The single Options / OI panel entry: one "Index Overlays" parent row
 *  with a sub-toggle per overlay (children visible while the parent is
 *  on, per the panel's subToggles pattern). */
export const OVERLAY_PANEL_ENTRY = {
  id: OVERLAY_PARENT_ID,
  label: 'Index Overlays',
  description: 'Rebased quote/tape overlays on the ES scale (SPX SPY cash quotes; NQ RTY YM ZN futures tape; VIX compressed 10:1; relative divergence only)',
  subToggles: UNDERLYING_OVERLAYS.map((o) => ({
    id: o.toggleId, label: o.style.title,
    desc: [o.description, `${o.style.title} overlay visible`],
  })),
};

/**
 * Build the chart's quoteOverlays prop ({key: bool}) from an indicators
 * state object: parent AND child. Callers wrap this in useMemo keyed on
 * the OVERLAY_STATE_IDS scalar values (never the indicators identity).
 */
export const buildQuoteOverlays = (indicators) => Object.fromEntries(
  UNDERLYING_OVERLAYS.map((o) => [
    o.key,
    indicators[OVERLAY_PARENT_ID] === true && indicators[o.toggleId] === true,
  ])
);

export default UNDERLYING_OVERLAYS;
